
import React, { useState } from 'react';
import { CheckInQuestion } from '../data/lessons';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  SmilePlus, 
  Smile, 
  Meh, 
  Frown, 
  Angry
} from 'lucide-react';
import { toast } from "sonner";
import analytics from '@/utils/analytics';
import { supabase } from "@/integrations/supabase/client";
import { useUser } from '@/context/UserContext';

interface CheckInFormProps {
  question: CheckInQuestion;
  onSubmit: (answer: string) => void;
  lessonDay: number;
}

const CheckInForm: React.FC<CheckInFormProps> = ({ question, onSubmit, lessonDay }) => {
  const [answer, setAnswer] = useState('');
  const [moodRating, setMoodRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const responseValue = question.type === 'mood' ? String(moodRating) : answer;
      
      // Save mood data if this is a mood question
      if (question.type === 'mood' && moodRating > 0 && user?.id) {
        const moodLabels = ['Very Poor', 'Poor', 'Neutral', 'Good', 'Excellent'];
        
        await supabase.from('mood_logs').insert({
          user_id: user.id,
          mood_rating: moodRating,
          mood_label: moodLabels[moodRating - 1],
          associated_lesson: lessonDay,
          notes: 'Recorded from daily check-in'
        });
      }
      
      // For tracking
      analytics.trackUserAction('check_in_submitted', {
        questionType: question.type,
        lessonDay
      });
      
      // Continue with original submit handler
      onSubmit(responseValue);
    } catch (error) {
      console.error('Error saving check-in data:', error);
      toast.error('There was an error saving your check-in data');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const moodIcons = [
    { icon: Angry, label: 'Very Poor' },
    { icon: Frown, label: 'Poor' },
    { icon: Meh, label: 'Neutral' },
    { icon: Smile, label: 'Good' },
    { icon: SmilePlus, label: 'Excellent' }
  ];
  
  return (
    <div className="bg-white p-6 rounded-2xl border border-hormony-beige/50 shadow-sm animate-slide-up">
      <h3 className="text-xl font-medium text-hormony-charcoal mb-4">
        Daily Check-In
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-4">
          <p className="text-hormony-charcoal/80 mb-3">{question.question}</p>
          
          {question.type === 'multiple-choice' && question.options && (
            <div className="space-y-2">
              {question.options.map((option, index) => (
                <label 
                  key={index} 
                  className={`block p-3 rounded-lg border transition-all cursor-pointer hover:bg-hormony-beige/10 ${
                    answer === option 
                      ? 'border-black bg-hormony-beige/20' 
                      : 'border-hormony-beige/50'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="option"
                      value={option}
                      checked={answer === option}
                      onChange={() => setAnswer(option)}
                      className="sr-only"
                    />
                    <div className={`h-5 w-5 rounded-full border flex items-center justify-center mr-3 ${
                      answer === option 
                        ? 'border-black bg-black' 
                        : 'border-hormony-charcoal/30'
                    }`}>
                      {answer === option && (
                        <div className="h-2 w-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span>{option}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
          
          {question.type === 'text' && (
            <Textarea
              className="w-full min-h-[120px]"
              placeholder="Share your thoughts..."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          )}
          
          {question.type === 'mood' && (
            <div className="py-4">
              <div className="flex justify-between mb-3">
                {moodIcons.map((mood, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setMoodRating(index + 1)}
                    className={`flex flex-col items-center transition-all ${
                      moodRating === index + 1 
                        ? 'scale-110 text-black' 
                        : 'text-hormony-charcoal/50 hover:text-hormony-charcoal/80'
                    }`}
                  >
                    <mood.icon size={32} />
                    <span className="text-xs mt-1">{mood.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <Button
          type="submit"
          className="w-full bg-black hover:bg-gray-800 text-white"
          disabled={(question.type !== 'mood' && !answer) || (question.type === 'mood' && !moodRating) || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit & Complete Today\'s Lesson'}
        </Button>
      </form>
    </div>
  );
};

export default CheckInForm;
